import { useEffect, useState } from "react";

/**
 * useDebounce — defer state propagation by `delay` milliseconds.
 *
 * Ported from v1 (`ando-frontend/app/hooks/useDebounce.ts`-equivalent pattern,
 * v1 leans on the `use-debounce` lib for callback debouncing; we keep our
 * own minimal hook for state debouncing to avoid an extra dep in v2).
 *
 * @example
 *   const [query, setQuery] = useState("");
 *   const debounced = useDebounce(query, 350);
 *   useEffect(() => { fetchResults(debounced); }, [debounced]);
 */
export function useDebounce<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState<T>(value);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);

  return debounced;
}
